import React, { useEffect, useState } from 'react'
import { muiPlus } from '@ally/metronome-icons'
import {
  Icon,
  Modal,
  useModalContext,
  Button,
  Box,
  get,
  TextHeading,
  Space,
  Spinner,
  Alert,
} from '@ally/metronome-ui'
import allytm from '@ally/allytm'
import styled from 'styled-components'
import { AccountDetails, Beneficiary, IraPlan } from '../../../types'
import { useBeneficiariesApi } from '../../../api/beneficiaries-api'
import { AddBeneTypeSelection } from './AddBeneTypeSelection'
import { AddExistingBene } from './AddExistingBene'
import { AddNewBene } from '.'
import { OtherFdicModalContent } from './OtherFdicModalContent'
import { useIraBeneficiaries } from '../../../services/IraBeneficiariesProvider'

const StyledIconText = styled(Box)`
  padding-left: ${get.space('xs')};
`

const StyledSpinner = styled(Spinner)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
`
const headerRef = React.createRef<HTMLHeadingElement>()
export interface AddBeneficiary {
  addType: string
  beneficiarySelection?: Beneficiary
  isMarried?: string
}

const AddButton: React.FC<{
  hasExistingBenes: boolean
  isIraPage: boolean
  accountId: string
  accountLabel: string
}> = ({ hasExistingBenes, isIraPage, accountId, accountLabel }) => {
  const { setOpen } = useModalContext()
  const { isCommunityPropertyState } = useIraBeneficiaries()
  return (
    <Button
      onClick={(): void => {
        if (isCommunityPropertyState) {
          allytm.event(
            'modalView',
            'AOS:Beneficiaries:Bank IRAs:Add Beneficiary:Marriage Designation',
            {
              treatAsPageview: true,
            },
          )
        } else
          allytm.event(
            'modalView',
            isIraPage
              ? 'AOS:Beneficiaries:Bank IRAs:Add Beneficiary'
              : 'AOS:Beneficiaries:Bank Checking Savings and CDs:Add Beneficiary',
            {
              treatAsPageview: true,
            },
          )
        setOpen(true)
      }}
      unsupportedContent={
        <>
          <Icon ariaHidden icon={muiPlus} size="xl" />{' '}
          <StyledIconText>
            {!hasExistingBenes && isIraPage
              ? 'Add primary beneficiary'
              : `Add beneficiary`}
          </StyledIconText>
        </>
      }
      variant="link"
      data-testid="add-bene-modal-trigger"
      id={`${accountId}-add-bene-success-focus`}
      allytmln={`add beneficiary-${accountLabel}`}
    />
  )
}

export const AddBeneficiaryModalContent: React.FC<{
  account: AccountDetails | IraPlan
  isIraPage: boolean
  hasExistingBenes: boolean
  addBeneficiary: AddBeneficiary
  setAddBeneficiary: React.Dispatch<React.SetStateAction<AddBeneficiary>>
}> = ({
  account,
  isIraPage,
  hasExistingBenes,
  addBeneficiary,
  setAddBeneficiary,
}) => {
  const [formError, setFormError] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [uniqueBenes, setUniqueBenes] = useState<Beneficiary[]>([])
  const [displayOtherFdicModalContent, setDisplayOtherFdicModalContent] =
    useState(false)
  const [classificationToOther, setClassificationToOther] = useState(false)
  const { isCommunityPropertyState } = useIraBeneficiaries()

  const distributionType = isIraPage ? '' : account.distributionType
  const [ownershipTypeSelection, setOwnershipTypeSelection] = useState(
    distributionType || '',
  )

  const accountId: string = isIraPage
    ? (account.planId as string)
    : account.accountId

  const { fetchUniqueBeneficiaries } = useBeneficiariesApi()

  const getUniqueBeneficiaries = async (): Promise<void> => {
    setIsLoading(true)
    try {
      const uniqueBeneResponse = await fetchUniqueBeneficiaries(
        accountId,
        isIraPage,
      )
      if (uniqueBeneResponse && uniqueBeneResponse.unique.beneficiaries) {
        setUniqueBenes(uniqueBeneResponse.unique.beneficiaries)
      }
    } catch (e) {
      // istanbul ignore next - low value
      setFormError(true)
      // istanbul ignore next - tagging
      allytm.event(
        'customError',
        'It’s us – we’re having a moment. We couldn’t load your beneficiaries right now. We’ll be quick about it, so check back in a bit.',
        '',
      )
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    getUniqueBeneficiaries()
  }, [])

  const showBeneTypeSelection =
    (!addBeneficiary.addType && uniqueBenes.length > 0) ||
    (!addBeneficiary.addType && isCommunityPropertyState)

  const showAddNew =
    addBeneficiary.addType === 'add-new' ||
    (!uniqueBenes.length && !isCommunityPropertyState)

  // Low value test
  /* istanbul ignore next */
  if (isLoading) {
    return (
      <Space mb="md">
        <TextHeading
          tag="h1"
          size="sm"
          weight="bold"
          ref={headerRef}
          tabIndex={-1}
          id="add-bene-heading"
        >
          {isIraPage && !hasExistingBenes
            ? 'Add primary beneficiary'
            : 'Add beneficiary'}
        </TextHeading>
        <StyledSpinner />
      </Space>
    )
  }

  if (displayOtherFdicModalContent) {
    return (
      <OtherFdicModalContent
        setDisplayOtherFdicModalContent={setDisplayOtherFdicModalContent}
        setAddBeneficiary={setAddBeneficiary}
        setClassificationToOther={setClassificationToOther}
      />
    )
  }

  return (
    <Space mb="md">
      <TextHeading
        tag="h1"
        size="sm"
        weight="bold"
        ref={headerRef}
        tabIndex={-1}
        data-testid="addBeneModalHeader"
        id="add-bene-heading"
      >
        {isIraPage && !hasExistingBenes
          ? 'Add primary beneficiary'
          : 'Add beneficiary'}
      </TextHeading>
      <>
        {formError ? (
          // istanbul ignore next - low value, TODO - fix skipped test
          <Box mb="md">
            <Alert
              variant="error"
              disableContainer
              body="It's us - we're having a moment. We couldn't load your beneficiaries right now. We'll be quick about it, so check back in a bit."
              focusOnMount
            />
          </Box>
        ) : (
          <>
            {showBeneTypeSelection && (
              <AddBeneTypeSelection
                setAddBeneficiary={setAddBeneficiary}
                listOfBene={uniqueBenes}
                hasExistingBenes={hasExistingBenes}
                isIraPage={isIraPage}
                setOwnershipTypeSelection={setOwnershipTypeSelection}
                existingBenes={account.beneficiaries}
              />
            )}
            {addBeneficiary.addType === 'from-existing' && (
              <AddExistingBene
                beneficiarySelection={addBeneficiary.beneficiarySelection}
                existingBenes={account.beneficiaries}
                isIraPage={isIraPage}
                account={account}
                distributionType={ownershipTypeSelection}
                isMarried={addBeneficiary.isMarried}
              />
            )}
            {showAddNew && (
              <AddNewBene
                isIraPage={isIraPage}
                existingBenes={account.beneficiaries}
                distributionType={ownershipTypeSelection}
                accountId={accountId}
                setDisplayOtherFdicModalContent={
                  setDisplayOtherFdicModalContent
                }
                classificationToOther={classificationToOther}
                isMarried={addBeneficiary.isMarried}
              />
            )}
          </>
        )}
      </>
    </Space>
  )
}

export const AddBeneficiaryModal: React.FC<{
  account: AccountDetails | IraPlan
  isIraPage: boolean
  accountLabel: string
}> = ({ account, isIraPage, accountLabel }) => {
  const hasExistingBenes = !!account.beneficiaries?.length
  const [addBeneficiary, setAddBeneficiary] = useState<AddBeneficiary>({
    addType: '',
    beneficiarySelection: undefined,
    isMarried: '',
  })
  const accountId: string = isIraPage
    ? (account.planId as string)
    : account.accountId
  return (
    <Modal
      trigger={
        <AddButton
          isIraPage={isIraPage}
          hasExistingBenes={hasExistingBenes}
          accountId={accountId}
          accountLabel={accountLabel}
        />
      }
      content={
        <AddBeneficiaryModalContent
          account={account}
          isIraPage={isIraPage}
          hasExistingBenes={hasExistingBenes}
          addBeneficiary={addBeneficiary}
          setAddBeneficiary={setAddBeneficiary}
        />
      }
      fullScreenMd
      width="md"
      disableCloseOnOverlayClick
      onOpenChange={(): void =>
        setAddBeneficiary({
          addType: '',
          beneficiarySelection: undefined,
          isMarried: '',
        })
      }
    />
  )
}
